import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, type Article } from '@/data/articles';
import { MAIN_SITE_URL } from '@/data/categories';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, BRAND_NAME, FAVICON_URL } from '@/lib/seo';

function FaqAccordion({ faq }: { faq: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
      {faq.map((item, idx) => {
        const isOpen = open === idx;
        return (
          <div key={idx}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : idx)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-background-100/60"
            >
              <span className="text-sm font-semibold text-foreground-900 md:text-base">{item.q}</span>
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-transform ${
                  isOpen ? 'rotate-180 bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-arrow-down-s-line text-sm" />
              </span>
            </button>
            {isOpen && (
              <div className="px-5 pb-5">
                <p className="text-sm leading-relaxed text-foreground-600 md:text-base">{item.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RelatedArticles({ currentSlug }: { currentSlug: string }) {
  const { t } = useTranslation();
  const related = articles.filter((a) => a.slug !== currentSlug).slice(0, 3);

  return (
    <div className="mt-12">
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t('article.related.title')}</h2>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {related.map((a) => (
          <Link
            key={a.slug}
            to={`/article/${a.slug}`}
            className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300"
          >
            <div className="h-28 w-full overflow-hidden">
              <img
                src={a.image}
                alt={a.title}
                title={a.title}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-4">
              <h3 className="text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">{a.title}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function PillarArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find((a) => a.slug === slug);

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        image: article.image,
        author: { '@type': 'Organization', name: BRAND_NAME },
        publisher: {
          '@type': 'Organization',
          name: BRAND_NAME,
          logo: { '@type': 'ImageObject', url: FAVICON_URL },
        },
        datePublished: article.date,
        dateModified: article.date,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/article/${article.slug}` },
      }
    : null;

  useEffect(() => {
    if (article) {
      document.title = article.title;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Article not found</h1>
          <p className="mt-3 text-sm text-foreground-600">The article you are looking for does not exist.</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            {t('article.backHome')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {articleJsonLd && <JsonLd data={articleJsonLd} />}
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" />
              {t('article.backHome')}
            </Link>
            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line" />
                {t('article.author')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.readTime}
              </span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          <div className="overflow-hidden rounded-lg border border-background-200/70">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="h-56 w-full object-cover object-top md:h-72"
            />
          </div>

          {/* Quick Answer */}
          <div className="mt-8 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5 md:p-6">
            <p className="text-sm font-bold uppercase tracking-wide text-accent-900">{t('article.quickAnswerLabel')}</p>
            <p className="mt-2 text-base leading-relaxed text-foreground-700">{article.quickAnswer}</p>
          </div>

          <p className="mt-8 text-base leading-relaxed text-foreground-700">{article.intro}</p>

          {article.sections.map((section) => (
            <div key={section.heading}>
              <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{section.heading}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-foreground-700">{p}</p>
              ))}
              {section.list && (
                <ul className="mt-5 space-y-2.5">
                  {section.list.map((item, i) => (
                    <li key={i} className="flex gap-2.5 rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* FAQ */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.faq.title')}</h2>
          <div className="mt-5">
            <FaqAccordion faq={article.faq} />
          </div>

          {/* Related */}
          <RelatedArticles currentSlug={article.slug} />

          {/* CTA */}
          <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100/60 p-6 text-center md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t('article.related.title')}</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">{t('article.related.text')}</p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex w-full items-center justify-center rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap sm:w-auto"
              >
                {t('article.backHome')}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 whitespace-nowrap sm:w-auto"
              >
                {t('article.ctaButton')}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}